import { UseQueryOptions } from '@tanstack/react-query';
import { PromotionsResponse } from './types';
import { AxiosMethods } from '../../../../enums';
import { ProductApi } from '../../../../enums/api/products';
import { axiosHelper } from '../../../axios-instance';

enum ProductsQueryKey {
  PRODUCT = 'product',
  SEARCH_PRODUCTS = 'search-products',
  REALTIME_INVENTORY = 'realtime-inventory',
  PROMOTIONS = 'promotions',
  CONFIGURATIONS = 'product-configurations'
}

interface GetProductQueryParams {
  productId: string;
}

export const getProductQuery = ({ productId }: GetProductQueryParams) => ({
  queryKey: [ProductsQueryKey.PRODUCT, productId],
  queryFn: () =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: ProductApi.getProduct.replace(
        ':productId',
        encodeURIComponent(productId)
      ),
      method: AxiosMethods.GET
    }),
  enabled: !!productId
});

interface GetPromotionsQueryParams {
  skus: string[];
}

export const getPromotionsQuery = ({
  skus
}: GetPromotionsQueryParams): UseQueryOptions<PromotionsResponse> => ({
  queryKey: [ProductsQueryKey.PROMOTIONS, skus],
  queryFn: () =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: ProductApi.getPromotions,
      method: AxiosMethods.POST,
      data: {
        filters: {
          field: '_id',
          operator: '$in',
          value: skus
        },
        offset: 0,
        limit: 100
      }
    })
});

interface ProductSearchFilter {
  field: string;
  operator: string;
  value: any;
}

interface ProductSearchSort {
  field: string;
  direction: 'asc' | 'desc';
}

interface SearchProductsQueryParams {
  searchStr?: string;
  limit: number;
  offset: number;
  filters?: ProductSearchFilter;
  sort?: ProductSearchSort[];
}

export const searchProductsQuery = ({
  searchStr,
  limit,
  offset,
  filters,
  sort
}: SearchProductsQueryParams) => ({
  queryKey: [
    ProductsQueryKey.SEARCH_PRODUCTS,
    searchStr,
    limit,
    offset,
    filters,
    sort
  ],
  queryFn: () =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: ProductApi.searchProducts,
      method: AxiosMethods.POST,
      data: {
        ...(searchStr && { searchStr }),
        limit,
        offset,
        ...(filters && { filters }),
        ...(sort && { sort })
      }
    })
});

interface GetProductLastSoldDetailsQueryParams {
  productId: string;
  accountId: string;
  addressId: string;
  limit?: number;
  offset?: number;
}

export const getProductLastSoldDetailsQuery = ({
  productId,
  accountId,
  addressId,
  limit = 10,
  offset = 0
}: GetProductLastSoldDetailsQueryParams) => ({
  queryKey: [ProductsQueryKey.PRODUCT, productId, accountId, addressId],
  queryFn: () =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: ProductApi.getProductLastSoldDetails,
      method: AxiosMethods.POST,
      data: {
        accountId,
        addressId,
        productId,
        limit,
        offset
      }
    }),
  enabled: !!productId && !!accountId && !!addressId,
  staleTime: 10 * 60 * 1000,
  cacheTime: 10 * 60 * 1000
});

export const getProductConfigurations = () => ({
  queryKey: [ProductsQueryKey.CONFIGURATIONS],
  queryFn: () =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: ProductApi.getConfigurations,
      method: AxiosMethods.GET
    })
});
